"use client";

import { useState } from "react";
import { MapPin, Maximize2, ExternalLink } from "lucide-react";
import type { RichMap } from "@/lib/caonabo/rich-content";

type Props = { map: RichMap };

const TERRA_LIGHT = "#D77F70";

/**
 * Renderiza un mapa Static (Google Static Maps API) con marcadores y polyline.
 * Si hay polyline encoded la dibuja. Si hay múltiples markers, ajusta zoom para
 * mostrarlos todos. La key se lee de NEXT_PUBLIC_GOOGLE_MAPS_KEY (pública).
 */
export default function ChatMapCard({ map }: Props) {
  const [expanded, setExpanded] = useState(false);
  const apiKey =
    typeof process !== "undefined"
      ? process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY ?? ""
      : "";

  if (!apiKey) {
    // Fallback: mostrar enlaces a Google Maps abriendo en nueva ventana
    return (
      <div
        className="my-2 rounded-2xl border overflow-hidden p-3"
        style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(139,58,46,0.25)" }}
      >
        <div className="flex items-center gap-2 mb-2">
          <MapPin size={14} style={{ color: TERRA_LIGHT }} />
          <span className="text-xs font-bold" style={{ color: TERRA_LIGHT }}>
            {map.title ?? "Ubicación"}
          </span>
        </div>
        {map.subtitle && <p className="text-[10px] text-text-muted mb-2">{map.subtitle}</p>}
        <div className="space-y-1">
          {map.markers.map((m, i) => (
            <a
              key={i}
              href={`https://www.google.com/maps?q=${m.lat},${m.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[11px] underline hover:text-[#D77F70]"
            >
              <ExternalLink size={10} />
              {m.label ?? `${m.lat.toFixed(5)}, ${m.lng.toFixed(5)}`}
            </a>
          ))}
        </div>
      </div>
    );
  }

  // Construir URL Google Static Maps
  const w = expanded ? 1000 : 500;
  const h = expanded ? 600 : 280;
  const size = `${w}x${h}`;
  const params: string[] = [];
  for (const m of map.markers) {
    const c = m.color ?? "red";
    const label = m.label ? `|label:${m.label.charAt(0).toUpperCase()}` : "";
    params.push(`markers=color:${c}${label}|${m.lat},${m.lng}`);
  }
  if (map.polyline) {
    params.push(`path=enc:${encodeURIComponent(map.polyline)}|color:0x8B3A2Eaa|weight:4`);
  } else if (map.routeCoords && map.routeCoords.length > 1) {
    const path = map.routeCoords.map(([lat, lng]) => `${lat},${lng}`).join("|");
    params.push(`path=color:0x8B3A2Eaa|weight:4|${path}`);
  }
  // Centro: si no hay polyline ni multi marker, centrar en primer marker
  if (map.markers.length === 1 && !map.polyline) {
    params.push(`center=${map.markers[0].lat},${map.markers[0].lng}`);
    params.push(`zoom=${map.zoom ?? 14}`);
  }
  const url = `https://maps.googleapis.com/maps/api/staticmap?size=${size}&scale=2&language=es&${params.join("&")}&key=${apiKey}`;

  // URL para abrir en Google Maps interactivo
  const interactiveUrl =
    map.markers.length === 1
      ? `https://www.google.com/maps?q=${map.markers[0].lat},${map.markers[0].lng}&z=${map.zoom ?? 14}`
      : `https://www.google.com/maps/dir/${map.markers.map((m) => `${m.lat},${m.lng}`).join("/")}`;

  return (
    <div
      className="my-2 rounded-2xl border overflow-hidden caonabo-fade-in"
      style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(139,58,46,0.25)" }}
    >
      {/* Header */}
      <div
        className="px-3 py-2 flex items-center justify-between gap-2"
        style={{
          background: "linear-gradient(to right, rgba(139,58,46,0.12), rgba(139,58,46,0.06))",
          borderBottom: "1px solid rgba(139,58,46,0.20)",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <MapPin size={14} style={{ color: TERRA_LIGHT }} className="shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-bold truncate" style={{ color: TERRA_LIGHT }}>
              {map.title ?? "Mapa"}
            </span>
            {map.subtitle && (
              <span className="text-[10px] text-text-muted truncate">{map.subtitle}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={interactiveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-lg transition-colors text-text-muted hover:text-[#D77F70]"
            title="Abrir en Google Maps"
          >
            <ExternalLink size={12} />
          </a>
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="p-1.5 rounded-lg transition-colors text-text-muted hover:text-[#D77F70]"
            title={expanded ? "Vista normal" : "Vista expandida"}
          >
            <Maximize2 size={12} />
          </button>
        </div>
      </div>

      {/* Imagen del mapa */}
      <a href={interactiveUrl} target="_blank" rel="noopener noreferrer" className="block">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={url}
          alt={map.title ?? "Mapa"}
          className="w-full h-auto block"
          style={{ maxHeight: expanded ? "600px" : "280px", objectFit: "cover" }}
        />
      </a>

      {/* Markers list (si hay >2) */}
      {map.markers.length > 2 && (
        <div className="px-3 py-2 text-[10px] text-text-muted border-t" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
          {map.markers.length} ubicaciones marcadas
        </div>
      )}
    </div>
  );
}
